import {useRuntimeConfig} from '#imports';
import {name} from '../common';
import type {MaltComLanguageConfig} from '../types';
import {useLocale} from './useLocale';
import {useLocalePrefix} from './useLocalePrefix';

const DEFAULT_LANGUAGE = 'en';
/**
 * Resolves the i18next language to load for the current request.
 *
 * On single-locale hosts, uses the locale cookie language code.
 * On www.malt.com with locale prefix strategy enabled, uses the URL prefix when present.
 * Otherwise on malt.com, uses the cookie language when it is listed in malt-com-languages.json.
 */
export function useResolveTranslationLanguage(): string {
    const {languageCode, isMultiLocaleHost} = useLocale();
    const {getLocalePrefix, getLocaleCodeFromPrefix} = useLocalePrefix();
    const i18nConfig = useRuntimeConfig().public[name];
    const supportedLanguages = (i18nConfig?.supportedLanguages ?? []) as MaltComLanguageConfig[];

    // if malt.com
    if (isMultiLocaleHost.value) {
        // if locale prefix strategy is enabled
        const isLocalePrefixStrategyEnabled = !!i18nConfig?.enableLocalePrefixStrategy;
        if (isLocalePrefixStrategyEnabled) {
            const localePrefix = getLocalePrefix();
            if (localePrefix) {
                const localeCode = getLocaleCodeFromPrefix(localePrefix);
                const languageCodeFromPrefix = localeCode?.split('-')[0];

                if (languageCodeFromPrefix) {
                    return languageCodeFromPrefix;
                }
            }
        }

        const cookieLanguage = languageCode.value;
        if (cookieLanguage && supportedLanguages.some(({code}) => code === cookieLanguage)) {
            return cookieLanguage;
        }

        return DEFAULT_LANGUAGE;
    }

    // if localized host
    return languageCode.value ?? DEFAULT_LANGUAGE;
}
